import { EmberAnimationPlan, EmberEquipment, EmberJoints, emberEquipmentKey, planEmberAnimation } from "game-common/ember_sprite/generate"
import { Frame } from "game-common/ember_sprite/raster"
import { BaseTexture, Rectangle, SCALE_MODES, Texture } from "pixi.js"

import { seedTextureContentBounds } from "./texture_content_bounds"

/** One Ember animation as textures, shared by every sprite drawing that humanoid doing that thing. */
export interface BakedEmberAnimation {
    textures: Texture[]
    frameMs: number
    loop: boolean
    strideCycle?: number
    /** For an attack on the move: where the gait's stride has got to when it ends (0..1). */
    gaitPhaseAfter?: number
    /** Per frame, where the head and fists are on it: effects that follow a body attach there. */
    joints: EmberJoints[]
    /** The arms hold a load in front: the sprite draws the carried item between the hands. */
    carrying?: boolean
    /** Per frame, the box round everything drawn on it (a portrait fits the figure, not the frame). */
    drawn: DrawnBounds[]
    headTops: (number | undefined)[]
}

export interface DrawnBounds {
    x: number
    y: number
    width: number
    height: number
}

interface Waiter {
    callback: (baked: BakedEmberAnimation | null) => void
    cancelled?: () => boolean
}

interface Bake {
    key: string
    plan: EmberAnimationPlan
    textures: Texture[]
    drawn: DrawnBounds[]
    headTops: (number | undefined)[]
    waiters: Waiter[]
}

const INTERVAL = 20

/**
 * Baking ahead of time (requestIdle) only draws a frame in the browser's idle time, and only when what
 * is left of it covers a frame's drawing (as it has been taking here) and this much more. Without idle
 * callbacks (Safari), one frame every IDLE_FALLBACK_MS at most, and only while frames are cheap.
 */
const IDLE_SLACK_MS = 1
const IDLE_FALLBACK_MS = 60
const IDLE_FALLBACK_MAX_FRAME_MS = 3

export const frameTexture = (frame: Frame): Texture =>
    Texture.fromBuffer(
        new Uint8Array(frame.pixels.buffer, frame.pixels.byteOffset, frame.pixels.byteLength),
        frame.width,
        frame.height,
        { scaleMode: SCALE_MODES.NEAREST },
    )

/**
 * A frame as a texture holding only the box round what is drawn on it (`drawn`), trimmed into the
 * whole frame: it measures, anchors and draws exactly as the whole frame does (Texture `orig` and
 * `trim`), but keeps none of the empty margin -- most of a frame, and more the bigger a held thing
 * makes it (an axe's 96px frame is ~86% margin). A blank frame keeps one transparent pixel.
 */
export const croppedFrameTexture = (frame: Frame, drawn: DrawnBounds): Texture => {
    const w = Math.max(1, drawn.width), h = Math.max(1, drawn.height)
    const x = drawn.width ? drawn.x : 0, y = drawn.width ? drawn.y : 0
    const pixels = new Uint8Array(w * h * 4)
    if (drawn.width) {
        for (let row = 0; row < h; row++) {
            const from = ((y + row) * frame.width + x) * 4
            pixels.set(frame.pixels.subarray(from, from + w * 4), row * w * 4)
        }
    }
    const base = BaseTexture.fromBuffer(pixels, w, h, { scaleMode: SCALE_MODES.NEAREST })
    return new Texture(base, new Rectangle(0, 0, w, h), new Rectangle(0, 0, frame.width, frame.height), new Rectangle(x, y, w, h))
}

/** The box round a frame's opaque pixels (empty at the origin for a blank frame). */
export const drawnBounds = ({ width, height, pixels }: Frame): DrawnBounds => {
    let left = width
    let top = height
    let right = -1
    let bottom = -1
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (!pixels[(y * width + x) * 4 + 3]) continue
            if (x < left) left = x
            if (x > right) right = x
            if (y < top) top = y
            bottom = y
        }
    }
    return right < 0 ? { x: 0, y: 0, width: 0, height: 0 } : { x: left, y: top, width: right - left + 1, height: bottom - top + 1 }
}

/**
 * Renders Ember animations into textures, a frame at a time, on a per-tick time budget -- the same
 * shape as the LPC compositor's queue, for the same reason: a frame hitch on weak hardware is worse
 * than a body that finishes appearing a few ticks later. Results are cached for the session; there
 * are a handful of named humanoids, so the whole set is a few megabytes.
 */
/** What the baker is holding: every baked animation's frames, kept for the session (none are freed). */
export interface BakerHeld {
    /** Bytes of pixels -- held once on the CPU and again on the GPU once a frame has been drawn. */
    bytes: number
    frames: number
    animations: number
}

export class EmberFrameBaker {
    private baked = new Map<string, BakedEmberAnimation>()
    private heldBytes = 0
    private heldFrames = 0
    private bakes = new Map<string, Bake>()
    private queue: Bake[] = []
    private timer: any
    /** Bakes made ahead of time, drawn only in idle time (requestIdle); one wanted now moves to `queue`. */
    private idleQueue: Bake[] = []
    private idleHandle: any
    /** What drawing a frame has been taking here, in ms (an average), to fit idle-time drawing to the time left. */
    private frameCostMs = 3
    budgetMs = 4
    now: () => number = () => (typeof performance !== "undefined" ? performance.now() : Date.now())

    // What the body holds and wears, and in what colours, is part of every frame, so of the key.
    private keyOf = (spriteName: string, animation: string, equipment: EmberEquipment) =>
        `${spriteName}/${animation}/${emberEquipmentKey(equipment)}`

    /** An animation already baked, now, or nothing (a screen rebuilt every keystroke can't wait a tick). */
    peek = (spriteName: string, animation: string, equipment: EmberEquipment = {}): BakedEmberAnimation | undefined =>
        this.baked.get(this.keyOf(spriteName, animation, equipment))

    request = (
        spriteName: string,
        animation: string,
        priority: boolean,
        callback: (baked: BakedEmberAnimation | null) => void,
        cancelled?: () => boolean,
        equipment: EmberEquipment = {},
    ): void => {
        const key = this.keyOf(spriteName, animation, equipment)
        const done = this.baked.get(key)
        if (done) {
            // Still asynchronous: the sprite's adopt path expects to run outside activateAnimation.
            setTimeout(() => !cancelled?.() && callback(done), 0)
            return
        }
        const inFlight = this.bakes.get(key)
        if (inFlight) {
            inFlight.waiters.push({ callback, cancelled })
            // Being baked ahead of time in idle moments, and wanted after all: into the queue proper.
            const idle = this.idleQueue.indexOf(inFlight)
            if (idle >= 0) {
                this.idleQueue.splice(idle, 1)
                if (priority) this.queue.unshift(inFlight)
                else this.queue.push(inFlight)
                if (!this.timer) this.timer = setInterval(this.process, INTERVAL)
            } else if (priority) this.promote(inFlight)
            return
        }
        const plan = planEmberAnimation(spriteName, animation, equipment)
        if (!plan) {
            setTimeout(() => !cancelled?.() && callback(null), 0)
            return
        }
        const bake: Bake = { key, plan, textures: [], drawn: [], headTops: [], waiters: [{ callback, cancelled }] }
        this.bakes.set(key, bake)
        if (priority) this.queue.unshift(bake)
        else this.queue.push(bake)
        if (!this.timer) this.timer = setInterval(this.process, INTERVAL)
    }

    /**
     * Bake an animation ahead of time, in the browser's idle moments only (see IDLE_SLACK_MS), so it is
     * there when wanted without ever taking time from a frame. Nothing waits on it; asked for properly
     * meanwhile (request), it joins the ordinary queue.
     */
    requestIdle = (spriteName: string, animation: string, equipment: EmberEquipment = {}): void => {
        const key = this.keyOf(spriteName, animation, equipment)
        if (this.baked.has(key) || this.bakes.has(key)) return
        const plan = planEmberAnimation(spriteName, animation, equipment)
        if (!plan) return
        const bake: Bake = { key, plan, textures: [], drawn: [], headTops: [], waiters: [] }
        this.bakes.set(key, bake)
        this.idleQueue.push(bake)
        this.scheduleIdle()
    }

    private scheduleIdle = () => {
        if (this.idleHandle !== undefined || !this.idleQueue.length) return
        const idle = typeof window !== "undefined" ? (window as any).requestIdleCallback : undefined
        this.idleHandle = idle ? idle(this.processIdle) : setTimeout(() => this.processIdle(undefined), IDLE_FALLBACK_MS)
    }

    /** Draw ahead-of-time frames while the idle time left covers one; without idle callbacks, one cheap frame. */
    private processIdle = (deadline?: { timeRemaining: () => number }) => {
        this.idleHandle = undefined
        let drawn = 0
        while (this.idleQueue.length) {
            const fits = deadline ? deadline.timeRemaining() >= this.frameCostMs + IDLE_SLACK_MS : drawn === 0 && this.frameCostMs <= IDLE_FALLBACK_MAX_FRAME_MS
            if (!fits) break
            const bake = this.idleQueue[0]
            if (this.bakeFrame(bake)) {
                this.idleQueue.shift()
                this.finish(bake)
            }
            drawn++
        }
        this.scheduleIdle()
    }

    /** Draw the next frame of `bake`, timing it; true when that was its last. */
    private bakeFrame = (bake: Bake): boolean => {
        const began = this.now()
        const frame = bake.plan.renderFrame(bake.textures.length)
        const drawn = drawnBounds(frame)
        const texture = croppedFrameTexture(frame, drawn)
        // Measured here, from the pixels in hand, so nothing asks the GPU for them (texture_content_bounds).
        seedTextureContentBounds(texture, drawn.width ? new Rectangle(drawn.x, drawn.y, drawn.width, drawn.height) : null)
        bake.textures.push(texture)
        bake.drawn.push(drawn)
        bake.headTops.push(frame.headTop)
        this.frameCostMs += (this.now() - began - this.frameCostMs) * 0.2
        return bake.textures.length >= bake.plan.frameCount
    }

    private promote = (bake: Bake) => {
        const at = this.queue.indexOf(bake)
        if (at > 0) {
            this.queue.splice(at, 1)
            this.queue.unshift(bake)
        }
    }

    process = (): void => {
        const start = this.now()
        while (this.queue.length) {
            const bake = this.queue[0]
            // Nobody is waiting any more: leave it unbaked rather than spend the budget on it.
            if (bake.waiters.every(w => w.cancelled?.())) {
                this.queue.shift()
                this.bakes.delete(bake.key)
                continue
            }
            if (this.bakeFrame(bake)) {
                this.queue.shift()
                this.finish(bake)
            }
            if (this.now() - start >= this.budgetMs) return
        }
        clearInterval(this.timer)
        this.timer = undefined
    }

    private finish = (bake: Bake) => {
        const { frameMs, loop, strideCycle, gaitPhaseAfter, carrying } = bake.plan
        const joints = bake.textures.map((_, i) => bake.plan.jointsAt(i))
        const baked: BakedEmberAnimation = { textures: bake.textures, frameMs, loop, strideCycle, gaitPhaseAfter, joints, carrying, drawn: bake.drawn, headTops: bake.headTops }
        this.baked.set(bake.key, baked)
        this.heldFrames += bake.textures.length
        for (const t of bake.textures) this.heldBytes += t.baseTexture.realWidth * t.baseTexture.realHeight * 4
        this.bakes.delete(bake.key)
        bake.waiters.forEach(w => !w.cancelled?.() && w.callback(baked))
    }

    /** How much is baked and held (the frame-rate readout shows it). */
    held = (): BakerHeld => ({ bytes: this.heldBytes, frames: this.heldFrames, animations: this.baked.size })

    /** Drain everything now (tests, and previews that want every frame at once). */
    flush = (): void => {
        const budget = this.budgetMs
        this.budgetMs = Infinity
        this.process()
        this.budgetMs = budget
    }

    clear = (): void => {
        this.baked.forEach(b => b.textures.forEach(t => t.destroy(true)))
        this.baked.clear()
        this.heldBytes = 0
        this.heldFrames = 0
        this.bakes.clear()
        this.queue = []
        this.idleQueue = []
        clearInterval(this.timer)
        this.timer = undefined
    }
}

export const emberFrameBaker = new EmberFrameBaker()
